import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Layout from '../components/Layout';
import ReviewBoard from '../components/tasks/ReviewBoard';
import MicButton from '../components/voice/MicButton';
import { orchard } from '../theme';
import useStore from '../store/useStore';
import { useLearnVisit } from '../components/learn/learnApi';

/* «Задачи»: a board — Думаю → Делаю → Готово — where tasks are dragged with
   a finger or the mouse (also reordered inside a column), plus «Обзор»: what
   is due and when, from everything in the app. */

const COLUMNS = [
  { id: 'todo', ru: 'Думаю', en: 'Thinking', icon: '💭', color: '#7A4988' },
  { id: 'inprogress', ru: 'Делаю', en: 'Doing', icon: '⚡', color: '#D98E04' },
  { id: 'done', ru: 'Готово', en: 'Done', icon: '✅', color: '#2E7D5B' },
];
const NEXT = { todo: 'inprogress', inprogress: 'done', done: 'todo' };

const PRIORITY = {
  low: { ru: 'Не срочно', en: 'Low', color: '#94a3b8' },
  medium: { ru: 'Обычно', en: 'Normal', color: '#f59e0b' },
  high: { ru: 'Важно', en: 'Important', color: '#ef4444' },
};
const PALETTE = [
  '#C2233E',
  '#7A4988',
  '#D98E04',
  '#2E7D5B',
  '#14b8a6',
  '#3b82f6',
  '#ec4899',
  '#64748b',
];
const BLANK = {
  title: '',
  description: '',
  priority: 'medium',
  color: '#C2233E',
  dueDate: '',
  studentId: '',
  status: 'todo',
};

const popDone = keyframes`
  0% { transform: scale(1); }
  35% { transform: scale(1.04); box-shadow: 0 0 0 6px rgba(46,125,91,0.25); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(46,125,91,0); }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
const dayDiff = (d) => {
  const a = new Date(d);
  const b = new Date();
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((a - b) / 86400000);
};

async function api(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
  return j;
}

export default function TaskBoard() {
  useLearnVisit('tasks');
  const { language } = useStore();
  const en = language === 'en';
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'board' ? 'board' : 'review';
  return (
    <Layout title={en ? 'Tasks' : 'Задачи'}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: orchard.fontDisplay,
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            flex: 1,
          }}
        >
          {en ? 'Tasks' : 'Задачи'}
        </Typography>
        <Tabs
          value={tab}
          onChange={(e, v) =>
            setParams(
              (p) => {
                p.set('tab', v);
                return p;
              },
              { replace: true }
            )
          }
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
            },
          }}
        >
          <Tab
            value="review"
            label={en ? 'What & when' : 'Обзор: что и когда'}
          />
          <Tab value="board" label={en ? 'Board' : 'Доска'} />
        </Tabs>
      </Box>
      {tab === 'review' ? (
        <ReviewBoard en={en} onOpenBoard={() => setParams({ tab: 'board' })} />
      ) : (
        <Board en={en} />
      )}
    </Layout>
  );
}

function Board({ en }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null); // task being edited / created
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(null); // { id, status, index }
  const [justDone, setJustDone] = useState('');
  const [showOldDone, setShowOldDone] = useState(false);
  const ghostRef = useRef(null);
  const dragRef = useRef(null);
  const dropRef = useRef(null); // { status, index } under the finger
  const scrollerRef = useRef(null);
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const load = useCallback(async () => {
    try {
      setTasks(await api('/api/tasks'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    api('/api/students')
      .then((d) => setStudents(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [load]);

  const byCol = useMemo(() => {
    const weekAgo = Date.now() - 7 * 86400000;
    const out = {};
    COLUMNS.forEach((c) => {
      out[c.id] = tasks
        .filter((t) => t.status === c.id)
        .filter(
          (t) =>
            c.id !== 'done' ||
            showOldDone ||
            new Date(t.updatedAt).getTime() > weekAgo
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    return out;
  }, [tasks, showOldDone]);
  const hiddenDone =
    tasks.filter((t) => t.status === 'done').length - byCol.done.length;

  /* ── changes ── */
  const persistColumn = async (status, ids) => {
    try {
      setTasks(
        await api('/api/tasks/reorder', {
          method: 'POST',
          body: JSON.stringify({ status, ids }),
        })
      );
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  const moveTo = (id, status, index = 0) => {
    const list = tasksRef.current;
    const t = list.find((x) => x._id === id);
    if (!t) return;
    const target = list
      .filter((x) => x.status === status && x._id !== id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const at = Math.max(0, Math.min(index, target.length));
    target.splice(at, 0, { ...t, status });
    const order = new Map(target.map((x, i) => [x._id, i]));
    setTasks(
      list.map((x) =>
        order.has(x._id)
          ? {
              ...x,
              status,
              order: order.get(x._id),
              ...(x._id === id ? { updatedAt: new Date().toISOString() } : {}),
            }
          : x
      )
    );
    if (status === 'done' && t.status !== 'done') {
      setJustDone(id);
      setTimeout(() => setJustDone(''), 900);
    }
    persistColumn(
      status,
      target.map((x) => x._id)
    );
  };

  const quickAdd = async (status, title) => {
    const text = title.trim();
    if (!text) return;
    try {
      const t = await api('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: text,
          status,
          priority: 'medium',
          color: '#C2233E',
        }),
      });
      // new tasks go to the top of their column
      setTasks((list) => [...list, t]);
      const ids = [
        t._id,
        ...tasksRef.current
          .filter((x) => x.status === status && x._id !== t._id)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((x) => x._id),
      ];
      persistColumn(status, ids);
    } catch (e) {
      setError(e.message);
    }
  };

  const save = async () => {
    if (!form || !form.title.trim()) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        dueDate: form.dueDate || null,
        studentId: form.studentId || null,
      };
      delete body._id;
      const saved = form._id
        ? await api(`/api/tasks/${form._id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : await api('/api/tasks', {
            method: 'POST',
            body: JSON.stringify(body),
          });
      setTasks((list) =>
        form._id
          ? list.map((t) => (t._id === saved._id ? saved : t))
          : [...list, saved]
      );
      setForm(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setTasks((list) => list.filter((t) => t._id !== id));
    setForm(null);
    await api(`/api/tasks/${id}`, { method: 'DELETE' }).catch((e) =>
      setError(e.message)
    );
  };

  /* ── drag & drop: mouse at once, a finger after a short hold ── */
  useEffect(() => {
    // while dragging with a finger the page must not scroll
    const stop = (e) => {
      if (dragRef.current && dragRef.current.active) e.preventDefault();
    };
    window.addEventListener('touchmove', stop, { passive: false });
    return () => window.removeEventListener('touchmove', stop);
  }, []);

  const targetAt = (x, y, id) => {
    const el = document.elementFromPoint(x, y);
    const col = el && el.closest('[data-col]');
    if (!col) return null;
    const status = col.getAttribute('data-col');
    const cards = [...col.querySelectorAll('[data-card]')].filter(
      (c) => c.getAttribute('data-card') !== id
    );
    let index = cards.length;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i].getBoundingClientRect();
      if (y < r.top + r.height / 2) {
        index = i;
        break;
      }
    }
    return { status, index };
  };

  const onCardPointerDown = (e, task) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest('button, a, input, textarea')) return;
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const d = {
      id: task._id,
      x0: e.clientX,
      y0: e.clientY,
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      active: false,
      touch: e.pointerType !== 'mouse',
      moved: false,
      timer: 0,
    };
    dragRef.current = d;
    const begin = () => {
      d.active = true;
      if (navigator.vibrate && d.touch) navigator.vibrate(12);
      const index = byCol[task.status].findIndex((t) => t._id === task._id);
      dropRef.current = { status: task.status, index };
      setDrag({ id: task._id, status: task.status, index, height: d.height });
      requestAnimationFrame(() => place(d.lastX ?? d.x0, d.lastY ?? d.y0));
    };
    const place = (x, y) => {
      if (ghostRef.current) {
        ghostRef.current.style.width = `${d.width}px`;
        ghostRef.current.style.transform = `translate(${x - d.dx}px, ${y - d.dy}px) rotate(2deg)`;
      }
    };
    if (d.touch) d.timer = setTimeout(begin, 230);
    const move = (ev) => {
      d.lastX = ev.clientX;
      d.lastY = ev.clientY;
      const far = Math.hypot(ev.clientX - d.x0, ev.clientY - d.y0);
      if (!d.active) {
        if (d.touch && far > 8) {
          cleanup(); // a scroll, not a drag
          return;
        }
        if (d.touch || far <= 5) return;
        begin(); // the mouse: dragging starts with the move — and this move counts
      }
      place(ev.clientX, ev.clientY);
      const t = targetAt(ev.clientX, ev.clientY, d.id);
      if (t) {
        dropRef.current = t;
        setDrag((cur) =>
          cur && (cur.status !== t.status || cur.index !== t.index)
            ? { ...cur, ...t }
            : cur
        );
      }
      // near the edges: scroll the columns (phone) or the page
      const sc = scrollerRef.current;
      if (sc && mobile) {
        if (ev.clientX > window.innerWidth - 36) sc.scrollLeft += 14;
        else if (ev.clientX < 36) sc.scrollLeft -= 14;
      }
      if (ev.clientY > window.innerHeight - 60) window.scrollBy(0, 12);
      else if (ev.clientY < 90) window.scrollBy(0, -12);
    };
    const up = (ev) => {
      const was = dragRef.current;
      cleanup();
      if (!was || !was.active) {
        if (
          !was ||
          (!was.moved &&
            Math.hypot(ev.clientX - was.x0, ev.clientY - was.y0) < 6)
        )
          openEdit(task);
        return;
      }
      const target = dropRef.current;
      dropRef.current = null;
      setDrag(null);
      if (target) moveTo(was.id, target.status, target.index);
    };
    const cleanup = () => {
      clearTimeout(d.timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      if (dragRef.current === d) dragRef.current = null;
    };
    const cancel = () => {
      cleanup();
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
  };

  const openEdit = (t) =>
    setForm({
      _id: t._id,
      title: t.title,
      description: t.description || '',
      priority: t.priority || 'medium',
      color: t.color || '#C2233E',
      dueDate: t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 10) : '',
      studentId: (t.studentId && t.studentId._id) || t.studentId || '',
      status: t.status,
    });

  const dragged = drag && tasks.find((t) => t._id === drag.id);

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ userSelect: drag ? 'none' : 'auto' }}>
      {error && (
        <Typography
          sx={{ color: 'error.main', mb: 1, fontWeight: 600 }}
          onClick={() => setError('')}
        >
          {error}
        </Typography>
      )}
      {mobile && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          {COLUMNS.map((c, i) => (
            <Chip
              key={c.id}
              label={`${c.icon} ${en ? c.en : c.ru} · ${byCol[c.id].length}`}
              onClick={() => {
                const sc = scrollerRef.current;
                if (sc)
                  sc.scrollTo({
                    left: sc.clientWidth * 0.86 * i,
                    behavior: 'smooth',
                  });
              }}
              sx={{
                fontWeight: 700,
                bgcolor: alpha(c.color, dark ? 0.25 : 0.1),
                color: dark ? '#fff' : c.color,
              }}
            />
          ))}
        </Box>
      )}
      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          overflowX: mobile ? 'auto' : 'visible',
          scrollSnapType: mobile && !drag ? 'x mandatory' : 'none',
          pb: 2,
          mx: mobile ? -2 : 0,
          px: mobile ? 2 : 0,
          scrollbarWidth: 'none',
        }}
      >
        {COLUMNS.map((c) => (
          <Column
            key={c.id}
            col={c}
            en={en}
            dark={dark}
            mobile={mobile}
            tasks={byCol[c.id]}
            drag={drag}
            justDone={justDone}
            onAdd={(title) => quickAdd(c.id, title)}
            onCardPointerDown={onCardPointerDown}
            onNext={(t) => moveTo(t._id, NEXT[t.status], 0)}
            footer={
              c.id === 'done' && (hiddenDone > 0 || showOldDone) ? (
                <Button
                  size="small"
                  onClick={() => setShowOldDone((v) => !v)}
                  sx={{ mt: 1 }}
                >
                  {showOldDone
                    ? en
                      ? 'Hide older'
                      : 'Скрыть старые'
                    : en
                      ? `Older done: ${hiddenDone}`
                      : `Старые готовые: ${hiddenDone}`}
                </Button>
              ) : null
            }
          />
        ))}
      </Box>
      <Button
        startIcon={<AddIcon />}
        onClick={() => setForm({ ...BLANK })}
        sx={{ mt: 1 }}
      >
        {en ? 'Task with details' : 'Задача с подробностями'}
      </Button>

      {/* the card under the finger */}
      {dragged && (
        <Box
          ref={ghostRef}
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1500,
            pointerEvents: 'none',
            willChange: 'transform',
          }}
        >
          <CardBody task={dragged} en={en} dark={dark} lifted />
        </Box>
      )}

      <TaskDialog
        form={form}
        setForm={setForm}
        saving={saving}
        onSave={save}
        onDelete={remove}
        students={students}
        en={en}
      />
    </Box>
  );
}

function Column({
  col,
  en,
  dark,
  mobile,
  tasks,
  drag,
  justDone,
  onAdd,
  onCardPointerDown,
  onNext,
  footer,
}) {
  const [text, setText] = useState('');
  const over = drag && drag.status === col.id;
  const items = tasks.filter((t) => !drag || t._id !== drag.id);
  const gap = (i) =>
    over && drag.index === i ? (
      <Box
        key={`gap${i}`}
        sx={{
          height: drag.height || 64,
          borderRadius: 3,
          border: '2px dashed',
          borderColor: alpha(col.color, 0.6),
          bgcolor: alpha(col.color, 0.06),
          transition: 'height 0.15s ease',
        }}
      />
    ) : null;
  return (
    <Box
      data-col={col.id}
      sx={{
        flex: mobile ? '0 0 86%' : '1 1 0',
        minWidth: 0,
        scrollSnapAlign: 'start',
        p: 1.25,
        borderRadius: 4,
        bgcolor: alpha(col.color, dark ? 0.12 : 0.05),
        border: '1px solid',
        borderColor: over
          ? alpha(col.color, 0.7)
          : alpha(col.color, dark ? 0.3 : 0.15),
        transition: 'border-color 0.15s ease, background 0.15s ease',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, mb: 1 }}
      >
        <Typography
          sx={{ fontWeight: 800, color: dark ? '#fff' : col.color, flex: 1 }}
        >
          {col.icon} {en ? col.en : col.ru}
        </Typography>
        <Chip
          label={tasks.length}
          size="small"
          sx={{
            height: 22,
            fontWeight: 800,
            bgcolor: col.color,
            color: '#fff',
          }}
        />
      </Box>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          onAdd(text);
          setText('');
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 1,
          pl: 1.25,
          pr: 0.25,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <InputBase
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={en ? 'New task…' : 'Новая задача…'}
          sx={{ flex: 1, fontSize: '0.92rem', py: 0.6 }}
          inputProps={{ enterKeyHint: 'done' }}
        />
        <MicButton
          en={en}
          size={34}
          onText={(t) => t && setText((cur) => (cur ? `${cur} ${t}` : t))}
          onError={() => {}}
        />
        {text.trim() && (
          <IconButton
            type="submit"
            size="small"
            sx={{ color: col.color }}
            aria-label={en ? 'Add' : 'Добавить'}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      <Stack spacing={1} sx={{ minHeight: 80 }}>
        {items.map((t, i) => [
          gap(i),
          <Box
            key={t._id}
            data-card={t._id}
            onPointerDown={(e) => onCardPointerDown(e, t)}
            sx={{
              touchAction: 'pan-y',
              cursor: 'grab',
              animation:
                justDone === t._id
                  ? `${popDone} 0.8s ease`
                  : `${rise} 0.3s ease both`,
              borderRadius: 3,
            }}
          >
            <CardBody task={t} en={en} dark={dark} onNext={() => onNext(t)} />
          </Box>,
        ])}
        {gap(items.length)}
        {!items.length && !over && (
          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.disabled',
              fontSize: '0.85rem',
              py: 3,
            }}
          >
            {col.id === 'done'
              ? en
                ? 'Drag finished tasks here'
                : 'Перетащите сюда сделанное'
              : en
                ? 'Empty'
                : 'Пусто'}
          </Typography>
        )}
      </Stack>
      {footer}
    </Box>
  );
}

function CardBody({ task, en, dark, onNext, lifted }) {
  const due = task.dueDate ? dayDiff(task.dueDate) : null;
  const overdue = due !== null && due < 0 && task.status !== 'done';
  const done = task.status === 'done';
  const next = COLUMNS.find((c) => c.id === NEXT[task.status]);
  return (
    <Box
      sx={{
        p: 1.25,
        pl: 1.5,
        borderRadius: 3,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid ${task.color || '#C2233E'}`,
        boxShadow: lifted
          ? '0 18px 40px -12px rgba(0,0,0,0.35)'
          : dark
            ? 'none'
            : '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: lifted ? undefined : '0 6px 18px -10px rgba(0,0,0,0.3)',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Typography
          sx={{
            flex: 1,
            fontWeight: 700,
            fontSize: '0.93rem',
            lineHeight: 1.35,
            textDecoration: done ? 'line-through' : 'none',
            opacity: done ? 0.6 : 1,
            wordBreak: 'break-word',
          }}
        >
          {task.title}
        </Typography>
        {onNext && next && (
          <Tooltip title={`→ ${en ? next.en : next.ru}`}>
            <IconButton
              size="small"
              onClick={onNext}
              sx={{ p: 0.4, mt: -0.25, color: next.color }}
            >
              {task.status === 'inprogress' ? (
                <span style={{ fontSize: 14 }}>✅</span>
              ) : (
                <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {task.description && (
        <Typography
          sx={{
            mt: 0.4,
            fontSize: '0.8rem',
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.description}
        </Typography>
      )}
      {(task.dueDate ||
        (task.studentId && task.studentId.name) ||
        task.priority === 'high') && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
          {task.priority === 'high' && !done && (
            <Chip
              size="small"
              label={en ? 'Important' : 'Важно'}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: alpha('#ef4444', 0.12),
                color: '#ef4444',
              }}
            />
          )}
          {task.dueDate && (
            <Chip
              size="small"
              icon={<AccessTimeIcon style={{ fontSize: 12 }} />}
              label={
                overdue
                  ? `${en ? 'overdue' : 'просрочено'} · ${fmtDate(task.dueDate)}`
                  : due === 0
                    ? en
                      ? 'today'
                      : 'сегодня'
                    : due === 1
                      ? en
                        ? 'tomorrow'
                        : 'завтра'
                      : fmtDate(task.dueDate)
              }
              color={
                overdue
                  ? 'error'
                  : due !== null && due <= 1 && !done
                    ? 'warning'
                    : 'default'
              }
              variant={overdue ? 'filled' : 'outlined'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {task.studentId && task.studentId.name && (
            <Chip
              size="small"
              icon={<PersonOutlineIcon style={{ fontSize: 13 }} />}
              label={task.studentId.name
                .split(/\s+/)
                .slice(0, 2)
                .reverse()
                .join(' ')}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function TaskDialog({ form, setForm, saving, onSave, onDelete, students, en }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <Dialog
      open={Boolean(form)}
      onClose={() => setForm(null)}
      maxWidth="sm"
      fullWidth
    >
      {form && (
        <>
          <DialogTitle sx={{ fontWeight: 800 }}>
            {form._id
              ? en
                ? 'Task'
                : 'Задача'
              : en
                ? 'New task'
                : 'Новая задача'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={en ? 'What to do' : 'Что сделать'}
                  fullWidth
                  value={form.title}
                  onChange={set('title')}
                  autoFocus
                />
                <MicButton
                  en={en}
                  onText={(t) =>
                    t &&
                    setForm((f) => ({
                      ...f,
                      title: f.title ? `${f.title} ${t}` : t,
                    }))
                  }
                  onError={() => {}}
                />
              </Box>
              <TextField
                label={en ? 'Details' : 'Подробности'}
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={set('description')}
              />
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>{en ? 'Column' : 'Колонка'}</InputLabel>
                  <Select
                    value={form.status}
                    label={en ? 'Column' : 'Колонка'}
                    onChange={set('status')}
                  >
                    {COLUMNS.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.icon} {en ? c.en : c.ru}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>{en ? 'Priority' : 'Важность'}</InputLabel>
                  <Select
                    value={form.priority}
                    label={en ? 'Priority' : 'Важность'}
                    onChange={set('priority')}
                  >
                    {Object.entries(PRIORITY).map(([k, v]) => (
                      <MenuItem key={k} value={k}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: v.color,
                            mr: 1,
                          }}
                        />
                        {en ? v.en : v.ru}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <TextField
                label={en ? 'Due date' : 'Срок'}
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.dueDate}
                onChange={set('dueDate')}
              />
              <FormControl fullWidth size="small">
                <InputLabel>
                  {en ? 'Student (optional)' : 'Ученик (необязательно)'}
                </InputLabel>
                <Select
                  value={form.studentId}
                  label={en ? 'Student (optional)' : 'Ученик (необязательно)'}
                  onChange={set('studentId')}
                >
                  <MenuItem value="">
                    {en ? '— none —' : '— без ученика —'}
                  </MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {PALETTE.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setForm((f) => ({ ...f, color: c }))}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: c,
                      cursor: 'pointer',
                      outline:
                        form.color === c
                          ? `3px solid ${alpha(c, 0.45)}`
                          : 'none',
                      outlineOffset: 2,
                    }}
                  />
                ))}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
            {form._id ? (
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => onDelete(form._id)}
              >
                {en ? 'Delete' : 'Удалить'}
              </Button>
            ) : (
              <span />
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setForm(null)}>
                {en ? 'Cancel' : 'Отмена'}
              </Button>
              <Button
                variant="contained"
                onClick={onSave}
                disabled={!form.title.trim() || saving}
              >
                {saving ? (
                  <CircularProgress size={16} />
                ) : en ? (
                  'Save'
                ) : (
                  'Сохранить'
                )}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
